import { noop } from 'lodash'
import PropTypes from 'prop-types'
import React from 'react'
import { Controller } from 'react-hook-form'
import { NumericFormat } from 'react-number-format'

import HHTextField from './HHTextField'

/**
 * A form number field component that integrates with react-hook-form.
 * This component wraps NumericFormat with react-hook-form's Controller for form state management,
 * providing formatted number input with various configuration options.
 *
 * @param {Object} props - The component props
 * @param {Object} [props.sx] - The system prop that allows defining system overrides as well as additional CSS styles
 * @param {string} props.name - The name of the field (required for form registration)
 * @param {Object} props.control - The react-hook-form control object
 * @param {boolean} [props.disabled=false] - Whether the field is disabled
 * @param {string} [props.label] - The label text for the field
 * @param {string} [props.placeholder='0'] - Placeholder text for the field
 * @param {('small'|'medium')} [props.size='medium'] - The size of the number field
 * @param {string} [props.autocomplete='off'] - The autocomplete attribute for the input
 * @param {boolean} [props.thousandSeparator=true] - Whether to use thousand separators in the number
 * @param {('.'|','|boolean)} [props.decimalSeparator='.'] - The decimal separator character
 * @param {number} [props.decimalScale=0] - The number of decimal places to allow
 * @param {('filled'|'outlined'|'standard')} [props.variant='outlined'] - The variant of the number field
 * @param {boolean} [props.allowNegative=true] - Whether to allow negative numbers
 * @param {Object} [props.rules] - The validation rules for react-hook-form
 * @param {boolean} [props.required=false] - Whether the field is required
 * @param {boolean} [props.fullWidth=false] - Whether the field should take up the full width of its container
 * @param {boolean} [props.valueIsNumericString=false] - Whether the value should be treated as a numeric string
 * @param {Object} [props.InputProps] - Props applied to the Input element
 * @param {React.ReactNode} [props.endLabelIcon] - Icon to display at the end of the label
 * @param {string} [props.error=''] - Error message to display
 * @param {boolean} [props.showErrorMessage=false] - Whether to show the error message
 * @param {Function} [props.onChange=noop] - Callback fired when the value changes
 * @param {boolean} [props.dollarPrefix] - Whether to show a dollar sign prefix
 * @param {boolean} [props.autoFocus] - Autofocus the input on mount (passes through to the underlying input)
 * @param {boolean} [props.deprecatedLabel] - Legacy label rendering toggle (passes through to HHTextField)
 * @param {string|number} [props.value] - The value of the field
 * @param {string|number} [props.defaultValue] - The default value of the field
 * @returns {React.ReactElement} The rendered form number field component
 */
const HHFormNumberField = ({
  sx = undefined,
  name,
  control,
  disabled = false,
  label = undefined,
  placeholder = '0',
  size = 'medium',
  autocomplete = 'off',
  thousandSeparator = true,
  decimalSeparator = '.',
  decimalScale = 0,
  variant = 'outlined',
  allowNegative = true,
  rules = undefined,
  required = false,
  fullWidth = false,
  valueIsNumericString = false,
  InputProps = undefined,
  endLabelIcon = undefined,
  error = '',
  showErrorMessage = false,
  onChange = noop,
  deprecatedLabel = true,
  ...rest
}) => {
  const isRequired = required || rules?.required === true
  return (
    <Controller
      control={control}
      name={name}
      // eslint-disable-next-line react/jsx-no-bind -- RHF Controller's render-prop API; not hoistable
      render={({ field }) => {
        const { ref, onChange: onChangeHookForm, onBlur, ..._rest } = field
        return (
          <NumericFormat
            allowNegative={allowNegative}
            autoComplete={autocomplete}
            customInput={HHTextField}
            decimalScale={decimalScale}
            decimalSeparator={decimalSeparator}
            deprecatedLabel={deprecatedLabel}
            disabled={disabled}
            endIcon={endLabelIcon}
            error={error}
            InputProps={{ ...InputProps, onBlur }}
            inputRef={ref}
            // The in-field label carries its own marker, the way HHFormTextField renders a required one.
            label={isRequired && !deprecatedLabel ? `${label}*` : label}
            placeholder={placeholder}
            required={isRequired}
            size={size}
            sx={sx}
            thousandSeparator={thousandSeparator}
            variant={variant}
            // eslint-disable-next-line react/jsx-no-bind -- value adapter between NumericFormat and RHF; closure over both onChange handlers
            onValueChange={values => {
              const { value } = values
              onChangeHookForm(value)
              onChange(value)
            }}
            {..._rest}
            {...rest}
            fullWidth={fullWidth}
            showErrorMessage={showErrorMessage}
            valueIsNumericString={valueIsNumericString}
          />
        )
      }}
      rules={rules}
    />
  )
}

HHFormNumberField.propTypes = {
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.func]),
  name: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium']),
  autocomplete: PropTypes.string,
  dollarPrefix: PropTypes.bool,
  decimalScale: PropTypes.number,
  thousandSeparator: PropTypes.bool,
  decimalSeparator: PropTypes.oneOf(['.', ',', false, true]),
  variant: PropTypes.oneOf(['filled', 'outlined', 'standard']),
  allowNegative: PropTypes.bool,
  rules: PropTypes.object,
  required: PropTypes.bool,
  fullWidth: PropTypes.bool,
  valueIsNumericString: PropTypes.bool,
  InputProps: PropTypes.object,
  endLabelIcon: PropTypes.node,
  error: PropTypes.string,
  showErrorMessage: PropTypes.bool,
  onChange: PropTypes.func,
  deprecatedLabel: PropTypes.bool,
}

export default HHFormNumberField
